import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import HelpButton from './Guides/HelpButton';
import { PageHeader, DataTableToolbar, PaginationBar, EmptyState, LoadingSpinner } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import InteractiveTable from '../components/InteractiveTable';
import { useFormatter } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';
import api, { getApiErrorMessage } from '../services/api';
import { toast } from 'sonner';
import { Plus, Download, Filter, FileText, BarChart3 } from 'lucide-react';
import ModuleReports from './Reports/ModuleReports';
import { REQUISITION_REPORTS } from './Reports/reportConfigs';
import RequisitionDashboard from './Requisitions/RequisitionDashboard';

const STATUS_STYLES = {
  DRAFT:      'bg-gray-100 text-gray-600',
  UNAPPROVED: 'bg-blue-100 text-blue-700',
  PENDING:    'bg-amber-100 text-amber-700',
  ORDER:      'bg-cyan-100 text-cyan-700',
  RFQ:        'bg-red-100 text-red-700',
  DORMANT:    'bg-emerald-100 text-emerald-700',
  TEMPLATE:   'bg-gray-200 text-gray-500',
};

const StatusBadge = ({ status }) => (
  <span className={`text-xs font-semibold px-0 ${STATUS_STYLES[status] || 'text-gray-500'}`}>
    {status}
  </span>
);

const FILTER_OPTIONS = ['', 'DRAFT', 'UNAPPROVED', 'PENDING', 'ORDER', 'RFQ', 'DORMANT', 'TEMPLATE'];

export default function RequestsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'dashboard';
  return (
    <AppShell>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Requisitions</h1>
          <HelpButton guideId="requisitions" label="Requisitions Guide" />
        </div>
      </div>
      <div className="flex gap-1 border-b mb-4" data-testid="req-tabs">
        {[{ id: 'dashboard', label: 'Analytics', icon: BarChart3 }, { id: 'list', label: 'Requisitions' }, { id: 'reports', label: 'Reports', icon: FileText }].map(t => (
          <button key={t.id} onClick={() => setSearchParams({ tab: t.id })} data-testid={`req-tab-${t.id}`}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === t.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}>
            {t.icon && <t.icon className="h-3.5 w-3.5" />} {t.label}
          </button>
        ))}
      </div>
      {activeTab === 'dashboard' && <RequisitionDashboard />}
      {activeTab === 'list' && <ReqListContent />}
      {activeTab === 'reports' && <ModuleReports reports={REQUISITION_REPORTS} apiPrefix="/api/module-reports/requisitions" moduleLabel="Requisitions" />}
    </AppShell>
  );
}

function ReqListContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fmt, fmtCurrency } = useFormatter();
  const { systemSettings } = useAuth();
  const currency = systemSettings?.system_currency || 'USD';
  const tz = systemSettings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [filterField, setFilterField] = useState('request_number');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [loading, setLoading] = useState(false);

  // Sync filter state when URL searchParams change (dashboard drill-down)
  useEffect(() => {
    const sp_status = searchParams.get('status') || '';
    const sp_search = searchParams.get('search') || '';
    if (sp_status !== statusFilter) setStatusFilter(sp_status);
    if (sp_search !== search) setSearch(sp_search);
    setPage(1);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasDashboardFilter = !!(searchParams.get('status') || searchParams.get('search'));
  const filterLabel = searchParams.get('label') || '';
  const clearDashboardFilters = () => {
    setStatusFilter(''); setSearch('');
    setSearchParams({ tab: 'list' });
  };
  const [sortState, setSortState] = useState({ field: 'request_number', dir: 'desc' });
  const handleSort = useCallback((field) => {
    setSortState(prev => prev.field === field ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { field, dir: 'asc' });
  }, []);

  const defaultCols = useMemo(() => [
    { key: 'request_number', label: 'Request Number', width: 150, sortField: 'request_number', visible: true },
    { key: 'ref_num', label: 'Reference Number', width: 140, sortField: 'ref_num', visible: true },
    { key: 'entry_date', label: 'Entry Date', width: 130, sortField: 'entry_date', visible: true },
    { key: 'status', label: 'Request Status', width: 130, sortField: 'status', visible: true },
    { key: 'request_net_total', label: `Request Net Value (${currency})`, width: 180, sortField: 'request_net_total', align: 'right', visible: true },
    { key: 'request_description', label: 'Request Description', width: 260, sortField: 'request_description', visible: true },
    { key: 'originator_name', label: 'Request Originator', width: 160, sortField: 'originator_name', visible: true },
  ], [currency]);

  const renderCell = useCallback((col, r) => {
    switch (col.key) {
      case 'request_number': return <span className="font-mono font-semibold text-zinc-900 text-sm">{r.request_number}</span>;
      case 'ref_num': return <span className="text-sm">{r.ref_num || '—'}</span>;
      case 'entry_date': return <span className="text-sm whitespace-nowrap">{r.entry_date ? new Date(r.entry_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : '—'}</span>;
      case 'status': return <StatusBadge status={r.status} />;
      case 'request_net_total': return <span className="tabular-nums text-sm">{fmt(r.request_net_total)}</span>;
      case 'request_description': return <span className="text-sm block truncate">{r.request_description}</span>;
      case 'originator_name': return <span className="text-sm">{r.originator_name}</span>;
      default: return <span className="text-sm">{r[col.key] != null ? String(r[col.key]) : '—'}</span>;
    }
  }, [fmt, tz]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/requisitions', {
        params: { search, filter_field: filterField, page, page_size: pageSize, status: statusFilter, sort_by: sortState.field, sort_dir: sortState.dir }
      });
      setRecords(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      if (err?.response?.status >= 500) {
        toast.error('Failed to load requisitions');
      } else if (!err?.response) {
        // Network error or timeout — no response at all
        toast.error('Connection error — please check your network and refresh the page');
      }
    }
    setLoading(false);
  }, [search, filterField, page, pageSize, statusFilter, sortState]);

  useEffect(() => { load(); }, [load]);

  const createNew = async () => {
    try {
      const res = await api.post('/api/requisitions', {});
      navigate(`/requests/${res.data.request_number}`);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to create')); }
  };

  return (
    <>
      {hasDashboardFilter && (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 mb-3">
          <BarChart3 className="h-3.5 w-3.5" />
          <span className="font-medium">Filtered{filterLabel ? `: ${filterLabel}` : ''}</span>
          <button onClick={clearDashboardFilters} className="ml-1 hover:text-blue-900 font-bold" data-testid="clear-req-filter">&times;</button>
        </div>
      )}
      <div className="flex items-center justify-between pb-4 border-b mb-4">
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">Total records: {pageSize} / {total}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={createNew} data-testid="req-add-button">
            <Plus className="h-4 w-4 mr-1" /> Add New
          </Button>
          <Button size="sm" variant="outline" onClick={async () => {
            try {
              const XLSX = await import('xlsx');
              const r = await api.get('/api/requisitions', { params: { page_size: 5000, sort_by: sortState.field, sort_dir: sortState.dir, search, status: statusFilter } });
              const data = (r.data.data || []).map(req => ({
                'Request Number': req.request_number, 'Reference': req.ref_num || '', 'Entry Date': req.entry_date ? new Date(req.entry_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : '',
                'Status': req.status, 'Net Value': req.request_net_total || 0, 'Description': req.request_description || '', 'Originator': req.originator_name || '',
              }));
              const ws = XLSX.utils.json_to_sheet(data);
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, 'Requisitions');
              XLSX.writeFile(wb, `Requisitions_${new Date().toISOString().split('T')[0]}.xlsx`);
            } catch { toast.error('Export failed'); }
          }}>
            <Download className="h-4 w-4 mr-1" /> Export
          </Button>
          <Button size="sm" variant="outline" title="Filter">
            <Filter className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Search bar */}
      <div className="flex items-center gap-2 mb-4">
        <input
          type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search..."
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm w-48 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          aria-label="Search"
        />
        <select value={filterField} onChange={e => setFilterField(e.target.value)}
          className="h-9 rounded-lg border border-input bg-background px-2 text-sm">
          <option value="request_number">Request Number</option>
          <option value="request_description">Description</option>
          <option value="originator_name">Originator</option>
        </select>
        <Button size="sm" onClick={load}>Search</Button>
        <Button size="sm" variant="outline" onClick={() => { setSearch(''); setPage(1); }}>X Clear</Button>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="ml-auto h-9 rounded-lg border border-input bg-background px-2 text-sm">
          <option value="">All Statuses</option>
          {FILTER_OPTIONS.filter(Boolean).map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="rounded-xl border bg-card">
        {loading ? <div className="py-12"><LoadingSpinner /></div> :
        records.length === 0 ? (
          <EmptyState title="No requisitions found"
            description="Create your first requisition by clicking Add New."
            action={<Button size="sm" onClick={createNew}>Add New</Button>}
          />
        ) : (
          <InteractiveTable
            tableId="req-listing"
            defaultCols={defaultCols}
            data={records}
            renderCell={renderCell}
            onRowClick={(row) => navigate(`/requests/${row.request_number}`)}
            sortState={sortState}
            onSort={handleSort}
            rowKey="request_number"
          />
        )}
        <div className="px-4">
          <PaginationBar page={page} pageSize={pageSize} total={total}
            onPageChange={setPage} onPageSizeChange={v => { setPageSize(v); setPage(1); }} />
        </div>
      </div>
    </>
  );
}
